import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import EdgeScroll from './EdgeScroll'

const ROLES = {
  // A real tablist: the panels below switch.
  tabs: {
    group: { role: 'tablist' },
    item: active => ({ role: 'tab', 'aria-selected': active }),
  },
  // Filter buttons over one list.
  filter: {
    group: { role: 'group' },
    item: active => ({ 'aria-pressed': active }),
  },
  // Section links that change the route.
  nav: {
    group: {},
    item: active => ({ 'aria-current': active ? 'page' : undefined }),
  },
}

/**
 * The one tab row: Barlow medium labels 48px tall, the active one in ink
 * underlined in cover. The row never wraps; on a narrow sheet it scrolls sideways and
 * the cut edge fades (`EdgeScroll`). `variant` sets the semantics: `tabs`
 * (tablist), `filter` (pressed buttons) or `nav` (route sections, rendered
 * in a `nav`). Each tab is `{id, label, icon?, badge?}`; `badge` prints
 * after the label (a count). `rule={false}` drops the hairline under the
 * row. The active tab is scrolled into the row's view (sideways only), so
 * a deep link never lands on a tab hidden past the cut edge.
 */
export default function Tabs({
  tabs,
  value,
  onChange,
  label,
  variant = 'tabs',
  rule = true,
  bleed = true,
  className = '',
}) {
  const roles = ROLES[variant] || ROLES.tabs
  const Group = variant === 'nav' ? 'nav' : 'div'
  const groupRef = useRef(null)

  useEffect(() => {
    const group = groupRef.current
    const scroller = group?.parentElement
    const active = group?.querySelector('[data-active="true"]')
    if (!scroller || !active) return
    // The scroller is `relative`, so offsetLeft is measured from its edge.
    const left = active.offsetLeft
    const right = left + active.offsetWidth
    const pad = 32
    if (left - pad < scroller.scrollLeft) {
      scroller.scrollLeft = Math.max(0, left - pad)
    } else if (right + pad > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollLeft = right + pad - scroller.clientWidth
    }
  }, [value])

  return (
    <EdgeScroll
      className={`${rule ? 'border-b border-ink-hair' : ''} ${
        bleed ? '-mx-4 sm:mx-0' : ''
      } ${className}`}
      innerClassName={bleed ? 'px-4 sm:px-0' : ''}
    >
      <Group
        ref={groupRef}
        {...roles.group}
        aria-label={label}
        className="flex flex-nowrap items-stretch gap-x-5 sm:gap-x-8 w-max min-w-full"
      >
        {tabs.map(tab => {
          const Icon = tab.icon
          const active = tab.id === value
          return (
            <button
              key={tab.id}
              type="button"
              {...roles.item(active)}
              data-active={active ? 'true' : undefined}
              onClick={() => onChange(tab.id)}
              className={`font-sans font-medium text-[15px] inline-flex shrink-0 items-center gap-1.5 h-pitch-2 border-b-2 whitespace-nowrap transition-colors ${
                active
                  ? 'text-ink border-cover'
                  : 'text-ink-mute border-transparent hover:text-ink'
              }`}
            >
              {Icon && <Icon size={16} aria-hidden="true" />}
              {tab.label}
              {tab.badge}
            </button>
          )
        })}
      </Group>
    </EdgeScroll>
  )
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.elementType,
      badge: PropTypes.node,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['tabs', 'filter', 'nav']),
  rule: PropTypes.bool,
  // Bleed the scroller to the phone's edges (the page gutter is 16px).
  bleed: PropTypes.bool,
  className: PropTypes.string,
}
